/**
 * "Why is this here?", answered on the thing itself.
 *
 * Every panel already has a guide behind the ⓘ in its header, and that is the right place to read
 * about a PANEL. It is the wrong place to answer a question somebody has while looking at one
 * question in a Sync round, at the co-op card, or at the box they are about to type a thank-you
 * into — going to the header, opening a guide and reading four paragraphs to find the one sentence
 * that justifies THIS is a trip nobody makes. So the justification is placed on the thing, one
 * muted line, and it expands where it stands.
 *
 * The bone is the fact ledger: a row expands into its own provenance. Which means three rules —
 *
 *   It is never a modal.       A modal takes the screen away from the thing being explained, which
 *                              is the one piece of context that makes the sentence mean anything.
 *   It expands IN PLACE.       The claim and its source appear under the row, indented off a
 *                              hairline, so the reader can see what the claim is attached to.
 *   It is collapsed by default. This is a footnote. A footnote that starts open is a paragraph,
 *                              and the screen is then mostly explanation.
 *
 * `Collapsible` (RNR, on `@rn-primitives/collapsible`) owns the open state and the roles, so the
 * trigger genuinely reports `expanded` to a screen reader rather than leaving a caret glyph to
 * imply it, and the answer is ABSENT while folded rather than hidden. Nothing is persisted: no
 * network, no "seen" bookkeeping, no store slice. Whether somebody has read a footnote is not
 * state worth keeping, and the panel guides already settled that argument — they open on click
 * only, and remember nothing.
 *
 * APP-LOCAL COMPOSITION, not a design-system component, and deliberately absent from
 * `_design.md`'s components: catalogue. The pattern is a Collapsible with a muted trigger line;
 * what makes this file this app's is the content — it reads `registry.ts` for the panel's own
 * claim and its default lead-in is about the RELATIONSHIP, neither of which another app inherits.
 * The system component underneath it is Collapsible, which the roster already carries.
 */
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { moduleById, type ScreenId } from '@/registry';
import * as React from 'react';
import { View } from 'react-native';

/**
 * The wording when a call site does not supply its own.
 *
 * Deliberately about the RELATIONSHIP rather than about the app ("Why this panel exists", "About
 * this"): the claim underneath is always a finding about couples, so a lead-in promising a feature
 * explanation mis-sells what opens.
 */
const DEFAULT_LINE = 'How this helps your relationship';

export function WhyThis({
  id,
  line,
  claim,
  source,
  className,
}: {
  id: ScreenId;
  line?: string;
  /**
   * What opens, where the panel's own claim is not the answer to the question the line asks.
   *
   * The co-op card is the case this exists for. It lives on two panels and borrows one of them for
   * its guide, so "Why a shared budget helps" expanded into the Scoreboard's research about play
   * and scoring bands — a label and a body about two different subjects, which is worse than no
   * footnote at all. A caller that overrides the LINE is usually asking a narrower question than
   * the panel does, and this is where it answers it.
   *
   * `source` goes with it and is optional: a citation is for a research finding, and a sentence
   * about how this app's own rule works has nobody to cite.
   */
  claim?: string;
  source?: string;
  className?: string;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const guide = moduleById(id)?.guide;
  const label = line ?? DEFAULT_LINE;
  const body = claim ?? guide?.claim;
  const cite = claim ? source : guide?.source;

  /**
   * A module with no guide gets the sentence and no affordance.
   *
   * `moduleById` returns undefined for an id that is not in the registry — a dark panel, or a
   * typo — and a caller that supplies no `claim` of its own then has nothing to expand into. A
   * chevron whose only possible outcome is an empty panel is worse than no chevron: it
   * reads as broken, and it teaches the reader that this control does nothing. Same convention as
   * every other absent control in the fleet — name the thing, drop the button.
   */
  if (!body) {
    return (
      <View className={cn('py-1', className)}>
        <Text variant="small" className="text-muted-foreground">
          {label}
        </Text>
      </View>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn('gap-1.5', className)}>
      {/* Full width rather than `self-start`: on a phone this is a one-line target, and a tap that
          lands beside the words has to still open it. */}
      <CollapsibleTrigger
        accessibilityHint={open ? 'Hides the research behind this' : 'Shows the research behind this'}
        className="w-auto self-auto py-1 active:opacity-70">
        {/* Geometric shapes, not emoji: U+25B8/U+25BE default to text presentation, so they take
            a colour class instead of rendering as a coloured glyph that outshouts the row it is a
            footnote on. The restyle spends its one gold on the caret — it is small enough not to
            shout and the only mark saying this muted line is a control. */}
        <Text variant="small" className="text-gold-text text-[10px]">
          {open ? '▾' : '▸'}
        </Text>
        {/* The rule under the words, not under the row. `self-start` inside a column shrinks the
            Text to its own width so the underline stops where the sentence does — the press target
            stays the full-width Pressable above, which is what a thumb needs. */}
        <View className="min-w-0 flex-1">
          <Text
            variant="small"
            className="text-muted-foreground border-line-strong self-start border-b pb-0.5 font-semibold">
            {label}
          </Text>
        </View>
      </CollapsibleTrigger>

      {/* Hanging off a hairline rather than boxed in a card: this is provenance attached to the
          row above it, and a card would make it a second thing on the screen. */}
      <CollapsibleContent className="border-border ml-1.5 gap-1 border-l pl-3">
        <Text variant="small" className="text-muted-foreground">
          {body}
        </Text>
        {cite ? (
          <Text variant="caption" className="text-muted-foreground">
            {cite}
          </Text>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}
